import { Component } from 'preact';
import { X } from 'lucide-react';
import { GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED } from '../../lib/medievalTheme';
import { playUI, UI } from '../../utils/arena/uiSounds';
import {
  isStandalone, canNativeInstall, onInstallabilityChange, promptInstall,
  isIos, wasInstallPromptDismissed, dismissInstallPrompt,
} from '../../utils/installPrompt';
import { t } from '../../localization/t';

// "Play as an app" banner for the phone hub. Android: one tap fires the
// native install dialog (via the stashed beforeinstallprompt event).
// iOS: Apple exposes no install API, so the tap opens a short themed
// instruction card for the manual Share route. Hidden once running
// standalone, and stays hidden after an explicit dismiss.

const CARD_STYLE = {
  background: 'linear-gradient(180deg, rgba(30,24,14,0.97) 0%, rgba(16,12,7,0.98) 100%)',
  border: `1px solid ${GOLD} 0.45)`,
  borderRadius: 'var(--radius-xl)',
  boxShadow: '0 8px 30px rgba(0,0,0,0.7)',
};

// Safari's share glyph: a square with an arrow rising out of it.
function ShareGlyph({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 15V3" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 11v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
    </svg>
  );
}

export default class AddToHomePrompt extends Component {
  state = { dismissed: wasInstallPromptDismissed(), showIosSteps: false, installable: canNativeInstall() };

  componentDidMount() {
    this.unsub = onInstallabilityChange(() => this.setState({ installable: canNativeInstall() }));
  }

  componentWillUnmount() {
    this.unsub?.();
  }

  handleTap = async () => {
    playUI(UI.CLICK);
    if (this.state.installable) {
      const accepted = await promptInstall();
      if (accepted) this.setState({ dismissed: true });
      return;
    }
    this.setState({ showIosSteps: true });
  };

  handleDismiss = (e) => {
    e.stopPropagation();
    playUI(UI.CANCEL);
    dismissInstallPrompt();
    this.setState({ dismissed: true, showIosSteps: false });
  };

  render() {
    // Nothing to offer: already an app, dismissed, or a platform where
    // neither the native prompt nor the iOS manual route applies.
    if (isStandalone() || this.state.dismissed) return null;
    if (!this.state.installable && !isIos()) return null;

    return (
      <>
        {/* Banner pill, bottom-center above the version text */}
        <div className="absolute left-1/2 -translate-x-1/2 z-20" style={{ bottom: '3rem' }}>
          <button
            type="button"
            className="flex items-center gap-2 px-4 transition-transform duration-100 active:translate-y-px"
            style={{ ...CARD_STYLE, minHeight: '2.75rem', borderRadius: 'var(--radius-full)' }}
            onClick={this.handleTap}
          >
            <span style={{ color: ACCENT_GOLD }}><ShareGlyph size={15} /></span>
            <span className="text-sm font-semibold" style={{ color: TEXT_PRIMARY }}>
              {this.state.installable ? t('Install Valkenhall as an app') : t('Play fullscreen: add to Home Screen')}
            </span>
            <span
              role="button"
              aria-label={t('Dismiss')}
              className="flex items-center justify-center -mr-2"
              style={{ width: '2rem', height: '2rem' }}
              onClick={this.handleDismiss}
            >
              <X size={16} color={TEXT_MUTED} />
            </span>
          </button>
        </div>

        {/* iOS manual instructions */}
        {this.state.showIosSteps ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.72)' }}
            onClick={() => this.setState({ showIosSteps: false })}
          >
            <div
              className="flex flex-col gap-3 px-6 py-5"
              style={{ ...CARD_STYLE, maxWidth: '22rem' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="arena-heading text-lg font-bold" style={{ color: TEXT_PRIMARY }}>
                {t('Add Valkenhall to your Home Screen')}
              </div>
              <ol className="flex flex-col gap-2 text-sm" style={{ color: TEXT_BODY }}>
                <li className="flex items-center gap-2">
                  <span className="font-bold" style={{ color: ACCENT_GOLD }}>1.</span>
                  {t('Tap the')} <span className="inline-flex align-middle" style={{ color: ACCENT_GOLD }}><ShareGlyph size={14} /></span> {t("Share button in Safari's toolbar")}
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-bold" style={{ color: ACCENT_GOLD }}>2.</span>
                  {t('Scroll down and tap "Add to Home Screen"')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-bold" style={{ color: ACCENT_GOLD }}>3.</span>
                  {t('Open Valkenhall from your Home Screen: fullscreen, no browser bars')}
                </li>
              </ol>
              <button
                type="button"
                className="arena-heading font-bold self-end px-5 transition-transform duration-100 active:translate-y-px"
                style={{
                  minHeight: '2.75rem',
                  border: `1px solid ${GOLD} 0.6)`,
                  borderRadius: 'var(--radius-lg)',
                  background: 'linear-gradient(180deg, rgba(60,46,24,0.9) 0%, rgba(34,26,14,0.95) 100%)',
                  color: TEXT_PRIMARY,
                }}
                onClick={() => { playUI(UI.CLICK); this.setState({ showIosSteps: false }); }}
              >
                {t('Got it')}
              </button>
            </div>
          </div>
        ) : null}
      </>
    );
  }
}
